/*
 * <bb-mobile-quick-add-modal> — mobile-only quick-add modal for product cards.
 *
 * On tap of a `.bb-mqa__trigger` button on a card, fetches
 * `${productUrl}?section_id=quick-add-mobile` (the Section Rendering API),
 * extracts <product-info> from the response, and renders it inside the
 * singleton modal in layout/theme.liquid.
 *
 * Dawn's <product-info>, <variant-selects>, <product-form>, and <quantity-input>
 * are reused unchanged. cart-add success → cartUpdate pubsub → modal closes.
 *
 * Race handling:
 *   - AbortController per fetch; hide() aborts the in-flight request.
 *   - Symbol() token per show(); resolutions bail if the token changed (rapid
 *     taps on different cards no longer paint the wrong product).
 *   - On hide() we remove children individually so the nested <product-info>
 *     runs its own disconnectedCallback and unsubscribes its pubsub listeners.
 *
 * Security:
 *   - data-product-url is allowlisted against /products/<handle> before fetch.
 *   - <script> tags are stripped from the parsed <product-info> before
 *     injection (Section Rendering API can emit them).
 *
 * Failure UX (per docs/plans/2026-06-10-feat-mobile-quick-add-fetch-failure-toast-plan.md):
 *   - The modal does NOT open on fetch failure. Failures surface via the
 *     <bb-mobile-quick-add-toast> singleton (bottom-of-viewport notification
 *     with a "View Full Details" PDP link).
 *   - 5-second soft timeout: if the fetch is still pending after 5s, the toast
 *     appears but the fetch keeps racing. If it eventually resolves while the
 *     same tap is still pendingToken, the toast dismisses and the modal opens.
 *   - 6-second auto-dismiss after the toast appears.
 */

const MQA_TRIGGER_SELECTOR = '.bb-mqa__trigger';
const PRODUCT_URL_PATTERN = /^\/products\/[a-z0-9_-]+(\?.*)?$/i;

// Show the toast at +5s if the fetch is still pending. The fetch is NOT aborted
// at the timeout — it keeps racing, and if it eventually resolves while the
// same trigger is still the active pending one we open the modal.
const TOAST_SOFT_TIMEOUT_MS = 5000;
// Auto-dismiss the toast 6s after it appears — long enough to read the message
// and tap the link, short enough not to linger after the user has moved on.
const TOAST_AUTO_DISMISS_MS = 6000;
// Match the bb-mqa-toast CSS transition duration so [hidden] is re-applied
// after the fade-out completes.
const TOAST_TRANSITION_MS = 200;

if (!customElements.get('bb-mobile-quick-add-modal')) {
  customElements.define(
    'bb-mobile-quick-add-modal',
    class BbMobileQuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[data-bb-mqa-content]');
        this.fetchCache = new Map(); // productUrl → Promise<HTMLElement>
        this.pendingToken = null;
        this.currentAbort = null;

        // Toast state — single global toast (latest call wins). Resolved in
        // connectedCallback so we don't depend on DOM order at construction.
        this.toastEl = null;
        this.toastTimeoutId = null;     // setTimeout id for the soft timeout
        this.toastAutoDismissId = null; // setTimeout id for the auto-dismiss
        this.toastHideTimeoutId = null; // setTimeout id for [hidden] re-apply
        this.toastVisible = false;
      }

      connectedCallback() {
        // Auto-close on successful ATC — Dawn's <product-form>.closest('quick-add-modal')
        // doesn't match our tag (exact-tag-name match in product-form.js), so we
        // subscribe to the cartUpdate event and hide ourselves.
        this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
          if (data.source !== 'product-form') return;
          if (!this.hasAttribute('open')) return;
          this.hide();
        });
        // ModalDialog's constructor wires #ModalClose-BbMobileQuickAdd (the
        // permanent close button in the singleton) to hide() automatically.

        // Toast wiring — close button and link both dismiss.
        this.toastEl = document.getElementById('BbMobileQuickAddToast');
        if (this.toastEl) {
          this.toastEl.addEventListener('click', (e) => {
            if (e.target.closest('[data-bb-mqa-toast-close]')) this.dismissToast();
            if (e.target.closest('[data-bb-mqa-toast-link]')) this.dismissToast();
          });
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsub) this.cartUpdateUnsub();
        if (this.currentAbort) this.currentAbort.abort();
      }

      hide() {
        if (this.currentAbort) this.currentAbort.abort();
        this.pendingToken = null;
        clearTimeout(this.toastTimeoutId);
        this.dismissToast();
        // Remove children individually so nested custom elements (especially
        // <product-info>) fire disconnectedCallback and unsubscribe their own
        // pubsub listeners — prevents "variant change after close" errors.
        while (this.modalContent.firstChild) this.modalContent.firstChild.remove();
        super.hide();
      }

      // ---------- toast ----------

      showToast(productUrl) {
        if (!this.toastEl) return;
        const link = this.toastEl.querySelector('[data-bb-mqa-toast-link]');
        if (link) link.href = productUrl;
        // Cancel any in-flight hide → [hidden] re-apply timer so re-show works.
        clearTimeout(this.toastHideTimeoutId);
        this.toastHideTimeoutId = null;
        this.toastEl.hidden = false;
        // Defer the class toggle so the browser registers the starting state
        // before transitioning — without this, the toast pops in instantly.
        requestAnimationFrame(() => {
          if (this.toastEl) this.toastEl.classList.add('bb-mqa-toast--visible');
        });
        this.toastVisible = true;
        clearTimeout(this.toastAutoDismissId);
        this.toastAutoDismissId = setTimeout(
          () => this.dismissToast(),
          TOAST_AUTO_DISMISS_MS
        );
      }

      dismissToast() {
        if (!this.toastEl) return;
        clearTimeout(this.toastAutoDismissId);
        this.toastAutoDismissId = null;
        this.toastEl.classList.remove('bb-mqa-toast--visible');
        this.toastVisible = false;
        // Re-apply [hidden] after the CSS transition completes. Guarded against
        // a re-show racing during the fade.
        clearTimeout(this.toastHideTimeoutId);
        this.toastHideTimeoutId = setTimeout(() => {
          if (this.toastEl && !this.toastEl.classList.contains('bb-mqa-toast--visible')) {
            this.toastEl.hidden = true;
          }
        }, TOAST_TRANSITION_MS);
      }

      // ---------- show ----------

      async show(opener) {
        const productUrl = opener.getAttribute('data-product-url') || '';
        if (!PRODUCT_URL_PATTERN.test(productUrl)) {
          console.warn('bb-mqa: rejected non-product URL', productUrl);
          return;
        }

        const token = Symbol('mqa-open');
        this.pendingToken = token;

        // Reset any toast state from a stale prior invocation.
        this.dismissToast();
        clearTimeout(this.toastTimeoutId);

        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        // Soft timeout: at +5s, if this call is still the active one AND the
        // fetch hasn't resolved, surface the toast. The fetch keeps running.
        this.toastTimeoutId = setTimeout(() => {
          if (this.pendingToken !== token) return; // superseded
          this.showToast(productUrl);
        }, TOAST_SOFT_TIMEOUT_MS);

        try {
          const productInfoEl = await this.fetchSection(productUrl);
          clearTimeout(this.toastTimeoutId);
          this.toastTimeoutId = null;

          if (this.pendingToken !== token) return;       // CANCELLED — newer tap won
          // SUCCESS path. If the toast happened to be visible (soft timeout
          // fired) dismiss it before opening the modal.
          if (this.toastVisible) this.dismissToast();
          this.modalContent.replaceChildren(document.importNode(productInfoEl, true));
          super.show(opener);
        } catch (err) {
          clearTimeout(this.toastTimeoutId);
          this.toastTimeoutId = null;

          if (this.pendingToken !== token) return;       // CANCELLED
          if (err.name === 'AbortError') return;         // hide() cancelled us
          // FAILURE path. Show the toast (skip if soft timeout already did).
          if (!this.toastVisible) this.showToast(productUrl);
        } finally {
          if (this.pendingToken === token) this.pendingToken = null;
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      fetchSection(productUrl) {
        const cached = this.fetchCache.get(productUrl);
        if (cached) return cached;

        this.currentAbort = new AbortController();
        const promise = fetch(`${productUrl}?section_id=quick-add-mobile`, {
          signal: this.currentAbort.signal,
        })
          .then((r) => {
            if (!r.ok) throw new Error(`bb-mqa: section fetch ${r.status}`);
            return r.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const el = doc.querySelector('product-info');
            if (!el) throw new Error('bb-mqa: <product-info> missing in section response');
            // Strip inline scripts before injection — Section Rendering API
            // can ship them and re-execution would leak duplicate pubsub
            // listeners across opens.
            el.querySelectorAll('script:not([type="application/json"])').forEach((s) => s.remove());
            return el;
          })
          .catch((err) => {
            this.fetchCache.delete(productUrl); // don't cache failures
            throw err;
          });

        this.fetchCache.set(productUrl, promise);
        return promise;
      }

    }
  );

  // Delegated click on document — one listener handles every card on the page.
  document.addEventListener('click', (e) => {
    const button = e.target.closest(MQA_TRIGGER_SELECTOR);
    if (!button) return;
    // CSS pointer-events isn't enough on touch — check the attribute.
    if (button.getAttribute('aria-disabled') === 'true') return;
    const modal = document.getElementById('BbMobileQuickAdd');
    if (modal) modal.show(button);
  });

  // Warm the cache on pointerdown — buys ~100-200ms perceived latency vs. waiting
  // for the click event.
  document.addEventListener(
    'pointerdown',
    (e) => {
      const button = e.target.closest(MQA_TRIGGER_SELECTOR);
      if (!button) return;
      const url = button.getAttribute('data-product-url');
      if (!url || !PRODUCT_URL_PATTERN.test(url)) return;
      const modal = document.getElementById('BbMobileQuickAdd');
      if (modal) modal.fetchSection(url).catch(() => {});
    },
    { passive: true }
  );
}
